import { marketingAttributionToken } from "../shared/marketing-attribution";
import { z } from "zod";

export type QualifierSelectionMode = "exact_vehicle" | "help_me_choose";
export type QualifierTimeline =
  | "within_7_days"
  | "within_30_days"
  | "within_90_days";
export type QualifierPriority =
  | "lowest_complete_price"
  | "exact_specification"
  | "fastest_delivery"
  | "minimal_dealer_interaction";

export interface QualifierPrefill {
  version: 2;
  selectionMode: QualifierSelectionMode;
  timeline: QualifierTimeline;
  successPriority: QualifierPriority;
  completedAt: string;
}

export type MarketingEventName =
  | "page_viewed"
  | "cta_clicked"
  | "qualifier_started"
  | "qualifier_answered"
  | "qualifier_completed"
  | "signup_started";

const sessionKey = "buying-lane:marketing-session";
const attributionKey = "buying-lane:marketing-attribution";
const qualifierKey = "buying-lane:qualifier-prefill";
const trackedPagePaths = new Set([
  "/",
  "/how-it-works",
  "/pricing",
  "/sample-offer",
  "/faq",
  "/start",
  "/login",
  "/signup",
]);

type Attribution = Partial<{
  utmSource: string;
  utmMedium: string;
  utmCampaign: string;
  utmTerm: string;
  utmContent: string;
  referrerHost: string;
}>;

const attributionSchema = z.object({
  utmSource: z.string().optional(),
  utmMedium: z.string().optional(),
  utmCampaign: z.string().optional(),
  utmTerm: z.string().optional(),
  utmContent: z.string().optional(),
  referrerHost: z.string().optional(),
});
const qualifierPrefillSchema = z.object({
  version: z.literal(2),
  selectionMode: z.enum(["exact_vehicle", "help_me_choose"]),
  timeline: z.enum(["within_7_days", "within_30_days", "within_90_days"]),
  successPriority: z.enum([
    "lowest_complete_price",
    "exact_specification",
    "fastest_delivery",
    "minimal_dealer_interaction",
  ]),
  completedAt: z.string().refine((value) => !Number.isNaN(Date.parse(value))),
});

function hasBrowserWindow(): boolean {
  return "window" in globalThis;
}

function storage(kind: "local" | "session"): Storage | null {
  if (!hasBrowserWindow()) return null;
  try {
    return kind === "local" ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function marketingSessionId(): string {
  const target = storage("session");
  const existing = target?.getItem(sessionKey);
  if (existing && /^[a-zA-Z0-9-]{12,80}$/u.test(existing)) return existing;
  const created = crypto.randomUUID();
  target?.setItem(sessionKey, created);
  return created;
}

function readAttribution(): Attribution {
  const target = storage("session");
  try {
    const parsed = attributionSchema.safeParse(
      JSON.parse(target?.getItem(attributionKey) ?? "{}"),
    );
    return parsed.success ? parsed.data : {};
  } catch {
    return {};
  }
}

function sanitizedAttribution(input: Attribution): Attribution {
  const utmSource = marketingAttributionToken(input.utmSource);
  const utmMedium = marketingAttributionToken(input.utmMedium);
  const utmCampaign = marketingAttributionToken(input.utmCampaign);
  const utmTerm = marketingAttributionToken(input.utmTerm);
  const utmContent = marketingAttributionToken(input.utmContent);
  const referrerHost = input.referrerHost?.trim().toLowerCase().slice(0, 160);
  const sanitized: Attribution = {};
  if (utmSource) sanitized.utmSource = utmSource;
  if (utmMedium) sanitized.utmMedium = utmMedium;
  if (utmCampaign) sanitized.utmCampaign = utmCampaign;
  if (utmTerm) sanitized.utmTerm = utmTerm;
  if (utmContent) sanitized.utmContent = utmContent;
  if (
    referrerHost &&
    /^[a-z0-9.-]+$/u.test(referrerHost) &&
    !referrerHost.includes("..") &&
    !referrerHost.startsWith(".") &&
    !referrerHost.endsWith(".")
  ) {
    sanitized.referrerHost = referrerHost;
  }
  return sanitized;
}

function captureAttribution(): Attribution {
  if (!hasBrowserWindow()) return {};
  const existing = sanitizedAttribution(readAttribution());
  const parameters = new URLSearchParams(window.location.search);
  const value = (name: string) => marketingAttributionToken(parameters.get(name));
  let referrerHost = existing.referrerHost;
  if (!referrerHost && document.referrer) {
    try {
      const host = new URL(document.referrer).hostname;
      if (host && host !== window.location.hostname) referrerHost = host.slice(0, 160);
    } catch {
      referrerHost = undefined;
    }
  }
  const captured: Attribution = { ...existing };
  const utmSource = value("utm_source");
  const utmMedium = value("utm_medium");
  const utmCampaign = value("utm_campaign");
  const utmTerm = value("utm_term");
  const utmContent = value("utm_content");
  if (utmSource) captured.utmSource = utmSource;
  if (utmMedium) captured.utmMedium = utmMedium;
  if (utmCampaign) captured.utmCampaign = utmCampaign;
  if (utmTerm) captured.utmTerm = utmTerm;
  if (utmContent) captured.utmContent = utmContent;
  if (referrerHost) captured.referrerHost = referrerHost;
  storage("session")?.setItem(attributionKey, JSON.stringify(captured));
  return captured;
}

export function trackMarketingEvent(
  eventName: MarketingEventName,
  properties: {
    ctaId?: string;
    qualifierStep?: number;
    answerCode?: string;
  } = {},
): void {
  if (!hasBrowserWindow()) return;
  const payload = {
    eventId: crypto.randomUUID(),
    sessionId: marketingSessionId(),
    eventName,
    pagePath: window.location.pathname,
    ...properties,
    ...captureAttribution(),
  };
  void fetch("/api/marketing/events", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => undefined);
}

export function installMarketingAnalytics(): () => void {
  if (!hasBrowserWindow()) return () => undefined;
  let lastPage = "";
  const trackPage = () => {
    const path = window.location.pathname;
    if (!trackedPagePaths.has(path)) return;
    if (path === lastPage) return;
    lastPage = path;
    trackMarketingEvent("page_viewed");
  };
  const trackClick = (event: MouseEvent) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const tracked = target.closest<HTMLElement>("[data-analytics-id]");
    const ctaId = tracked?.dataset.analyticsId;
    if (ctaId) trackMarketingEvent("cta_clicked", { ctaId });
  };
  captureAttribution();
  trackPage();
  document.addEventListener("click", trackClick, { capture: true });
  window.addEventListener("popstate", trackPage);
  return () => {
    document.removeEventListener("click", trackClick, { capture: true });
    window.removeEventListener("popstate", trackPage);
  };
}

export function saveQualifierPrefill(prefill: QualifierPrefill): void {
  storage("session")?.setItem(qualifierKey, JSON.stringify(prefill));
}

export function readQualifierPrefill(): QualifierPrefill | null {
  try {
    const parsed = qualifierPrefillSchema.safeParse(
      JSON.parse(storage("session")?.getItem(qualifierKey) ?? "null"),
    );
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function clearQualifierPrefill(): void {
  storage("session")?.removeItem(qualifierKey);
}
